import { baselineSubsections } from "./spec/onboardingSpec";
import type { OnboardingScenario, OnboardingSubsection, SubsectionId } from "./types";

export { baselineSubsections };

/**
 * Mock scenarios for the dedicated /onboarding page hub.
 *
 * One card per proof-point subsection in compliance-research/
 * Onboarding-Deliverables.md. Subsections nest under their single-parent
 * dependency (§B.5 under §B.1, §B.4 under §B.3, §C.2/§C.3 under §C.1, etc.)
 * and §D.1 stays top-level with a Preconditions chip-list because it
 * depends on three upstream sections (§B.1 + §B.4 + §B.5).
 *
 * Canonical Path A applicant (US LLC, 1 single-key Account wallet, 2 UBOs, no triggers):
 *   8 top-level cards — §A, §B.1 (with §B.5 nested), §B.2, §B.3 (with §B.4
 *   nested), §B.6, §B.7, §C.1, §D.1 (with §D.2 / §D.3 / §D.4 nested).
 *
 * Path A LP variant: adds top-level §C.4 → 9 top-level.
 * Path A multisig variant: §C.2 nests under §C.1 → still 8 top-level.
 * Path B variant: adds top-level §B.8 + §C.5 deferred → 10 top-level.
 *
 * SLA placeholder "2–3 business days" — real number TBD by BD/Legal.
 * Today is anchored to 2026-04-29 for stable relative-time strings.
 */

const SLA = "2–3 business days";
const ORG = "Crescent Wealth";

// Baseline subsection definitions live in `spec/onboardingSpec.ts` and
// are re-exported above. Everything below is mock-only: scenario
// fixtures + the helpers (patch, allComplete, allReviewing, rejectSlot,
// etc.) that overlay scenario-specific state on top of the baseline.

// ─── Patch / state-bulk helpers ──────────────────────────────────────────────

type SubsectionPatch = Partial<OnboardingSubsection>;

function patch(
    base: OnboardingSubsection[],
    overrides: Partial<Record<SubsectionId, SubsectionPatch>>
): OnboardingSubsection[] {
    return base.map((sec) => {
        const over = overrides[sec.id];
        if (!over) return { ...sec };
        const merged: OnboardingSubsection = { ...sec, ...over };
        if (over.status && over.status !== "NotStarted") {
            if (!("readinessHint" in over)) merged.readinessHint = undefined;
            if (!("prerequisiteLabel" in over)) merged.prerequisiteLabel = undefined;
        }
        return merged;
    });
}

const COMPLETION_SUMMARIES: Partial<Record<SubsectionId, string>> = {
    "use-case": "Mint + redeem · $2M–$10M",
    "legal-entity": "Crescent Wealth Holdings LLC · Delaware",
    "tax-identification": "EIN verified · W-9 on file",
    "signatory-authority": "IDV passed · manager resolution + LLC clause on file",
    "beneficial-ownership": "2 UBOs verified · BOIR filed",
    "sanctions-screening": "All clear · re-screening cadence active",
    "source-of-funds": "Token-sale audit on file",
    "wallet-primary": "1 address registered · verified by signature",
    msa: "MSA + disclosures + R&Ws signed Apr 27, 2026"
};

function allComplete(base: OnboardingSubsection[]): OnboardingSubsection[] {
    return base.map((sec) =>
        !sec.isApplicable
            ? { ...sec }
            : {
                  ...sec,
                  status: "Complete" as const,
                  slots: sec.slots.map((s) => ({
                      ...s,
                      reviewState: "Approved" as const
                  })),
                  readinessHint: undefined,
                  prerequisiteLabel: undefined,
                  estimateLabel: undefined,
                  satisfiedProofPoints: sec.totalProofPoints,
                  outstandingProofPoints: 0,
                  pendingReviewProofPoints: 0,
                  completionSummary: COMPLETION_SUMMARIES[sec.id] ?? sec.completionSummary
              }
    );
}

function allReviewing(base: OnboardingSubsection[]): OnboardingSubsection[] {
    return base.map((sec) => {
        if (!sec.isApplicable) return { ...sec };
        if (sec.treatment === "solomon-run") {
            return {
                ...sec,
                status: "InProgress" as const,
                slots: sec.slots.map((s) => ({
                    ...s,
                    reviewState: "PendingReview" as const
                })),
                readinessHint: undefined,
                prerequisiteLabel: undefined,
                slaLabel: sec.slaLabel,
                lastUpdatedLabel: "Started Apr 27",
                pendingReviewProofPoints: sec.totalProofPoints
            };
        }
        return {
            ...sec,
            status: "WaitingOnSolomon" as const,
            slots: sec.slots.map((s) => ({
                ...s,
                reviewState: "PendingReview" as const
            })),
            readinessHint: undefined,
            prerequisiteLabel: undefined,
            estimateLabel: undefined,
            slaLabel: undefined,
            lastUpdatedLabel: "Submitted Apr 27",
            pendingReviewProofPoints: sec.totalProofPoints
        };
    });
}

// ─── Per-slot review-state mutators ─────────────────────────────────────────
// Operate over a section's `slots` array. Used inside scenario builders to
// flip individual slots into Rejected / Approved / adhoc-requested states
// on top of the base allReviewing / allComplete pass.

/** Flip one slot in a section to `Rejected` with a reviewer note. Index
 *  is into `sec.slots`. The rest of the section's slots are left at
 *  whatever review state they had.
 *
 *  Defaults `rejectedAt` and `deadline` to mirror the adhoc-request
 *  scenarios so the rejection card has the same date footer + due-by
 *  treatment without having to thread the values through every
 *  scenario. Override via the optional 4th arg if a scenario needs
 *  different dates. */
function rejectSlot(
    sec: OnboardingSubsection,
    slotIdx: number,
    reviewerNote: string,
    rejection?: { rejectedAt: string; deadline?: string }
): OnboardingSubsection {
    return {
        ...sec,
        slots: sec.slots.map((s, i) =>
            i === slotIdx
                ? {
                      ...s,
                      reviewState: "Rejected" as const,
                      reviewerNote,
                      rejection: rejection ?? {
                          rejectedAt: "2026-04-29T15:30:00Z",
                          deadline: "2026-05-08T23:59:59Z"
                      }
                  }
                : s
        )
    };
}

/** Append an adhoc-requested slot to a section. The slot enters the
 *  section in `PendingReview`/unfulfilled state with the
 *  `issuerRequested` flag set. */
function withAdhocRequest(
    sec: OnboardingSubsection,
    request: {
        title: string;
        description: string;
        requestedAt: string;
        deadline?: string;
        severity?: "minor" | "substantive";
    }
): OnboardingSubsection {
    return {
        ...sec,
        slots: [
            ...sec.slots,
            {
                title: request.title,
                description: request.description,
                issuerRequested: {
                    requestedAt: request.requestedAt,
                    deadline: request.deadline,
                    severity: request.severity
                }
                // reviewState left undefined — slot is unfulfilled, not
                // yet submitted by the applicant. Once the applicant
                // submits, the slot transitions to PendingReview.
            }
        ]
    };
}

/** Flip a slot to `PendingReview` with a `versionHistory` entry
 *  showing a prior rejection. Used to mock the "resubmitted, awaiting
 *  re-review" scenario. */
function withResubmittedSlot(
    sec: OnboardingSubsection,
    slotIdx: number,
    priorRejection: {
        submittedAt: string;
        rejectedAt: string;
        rejectedReason: string;
        payload?: unknown;
    }
): OnboardingSubsection {
    return {
        ...sec,
        slots: sec.slots.map((s, i) =>
            i === slotIdx
                ? {
                      ...s,
                      reviewState: "PendingReview" as const,
                      versionHistory: [
                          {
                              submittedAt: priorRejection.submittedAt,
                              rejectedAt: priorRejection.rejectedAt,
                              rejectedReason: priorRejection.rejectedReason,
                              payload: priorRejection.payload ?? null
                          }
                      ]
                  }
                : s
        )
    };
}

// ─── Scenarios ───────────────────────────────────────────────────────────────

export const scenarioFirstVisit: OnboardingScenario = {
    name: "1. First visit",
    state: "NotStarted",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 29, 2026" },
    banner: {
        kind: "your_turn",
        headline: "Your turn. Start with Use Case to unlock the rest.",
        ctaLabel: "Start with Use Case",
        ctaTargetId: "use-case"
    },
    subsections: baselineSubsections()
};

export const scenarioOrgInProgress: OnboardingScenario = {
    name: "2. Use Case complete",
    state: "InProgress",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 24, 2026" },
    banner: {
        kind: "your_turn",
        headline: "Your turn. Entity standing is the next step.",
        ctaLabel: "Continue with Entity standing",
        ctaTargetId: "legal-entity"
    },
    subsections: patch(baselineSubsections(), {
        "use-case": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 3,
            completionSummary: COMPLETION_SUMMARIES["use-case"]
        },
        "legal-entity": {
            status: "InProgress",
            estimateLabel: "~5 min remaining",
            satisfiedProofPoints: 2,
            outstandingProofPoints: 2
        }
    })
};

export const scenarioWalletsWaitingOnYou: OnboardingScenario = {
    name: "3. Wallets · your turn",
    state: "InProgress",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 20, 2026" },
    banner: {
        kind: "your_turn",
        headline: "Your turn. Sign the wallet challenge to verify ownership.",
        ctaLabel: "Sign wallet challenge",
        ctaTargetId: "wallet-primary"
    },
    subsections: patch(baselineSubsections(), {
        "use-case": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 3,
            completionSummary: COMPLETION_SUMMARIES["use-case"]
        },
        "legal-entity": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 4,
            completionSummary: COMPLETION_SUMMARIES["legal-entity"]
        },
        "tax-identification": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 5,
            completionSummary: COMPLETION_SUMMARIES["tax-identification"]
        },
        "signatory-authority": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 4,
            completionSummary: COMPLETION_SUMMARIES["signatory-authority"]
        },
        "beneficial-ownership": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 12,
            completionSummary: COMPLETION_SUMMARIES["beneficial-ownership"]
        },
        "sanctions-screening": {
            status: "Complete",
            satisfiedProofPoints: 6,
            completionSummary: COMPLETION_SUMMARIES["sanctions-screening"]
        },
        "source-of-funds": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 3,
            completionSummary: COMPLETION_SUMMARIES["source-of-funds"]
        },
        "wallet-primary": {
            status: "WaitingOnYou",
            estimateLabel: "~5–15 min",
            attentionReason: "Declare the wallet: address, type, and the matching control proof.",
            outstandingProofPoints: 0
        }
    })
};

export const scenarioAllUnderReview: OnboardingScenario = {
    name: "6. Under review",
    state: "UnderReview",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 12, 2026", submitted: "Apr 27, 2026" },
    banner: {
        kind: "we_are_reviewing",
        headline: `We're reviewing. Typically ${SLA}; we'll email you when there's an update.`
    },
    subsections: allReviewing(baselineSubsections())
};

/** Some sections approved — post-submission, partial progress without
 *  rejections. Covers the UI state where some sections show the
 *  "Approved" badge while others are still under review. With
 *  PartiallyApproved removed, the partially-pending sections collapse
 *  to "Under review" — this scenario validates the mixed-badge layout. */
export const scenarioSomeApproved: OnboardingScenario = {
    name: "7. Some sections approved",
    state: "UnderReview",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 12, 2026", submitted: "Apr 27, 2026" },
    banner: {
        kind: "we_are_reviewing",
        headline: "We're reviewing. Some sections are already approved; the rest are in queue."
    },
    subsections: allReviewing(baselineSubsections()).map((sec) => {
        if (sec.id === "use-case" || sec.id === "legal-entity" || sec.id === "tax-identification") {
            return {
                ...sec,
                status: "Complete" as const,
                slots: sec.slots.map((s) => ({
                    ...s,
                    reviewState: "Approved" as const
                })),
                slaLabel: undefined,
                lastUpdatedLabel: "Approved Apr 30",
                satisfiedProofPoints: sec.totalProofPoints,
                pendingReviewProofPoints: 0
            };
        }
        return sec;
    })
};

export const scenarioApprovedCelebration: OnboardingScenario = {
    name: "11. Approved",
    state: "Approved",
    orgName: ORG,
    sla: SLA,
    lifecycle: {
        started: "Apr 12, 2026",
        submitted: "Apr 27, 2026",
        active: "Apr 29, 2026"
    },
    banner: { kind: "approved", headline: "You're live with YaaS." },
    celebrate: true,
    subsections: allComplete(baselineSubsections())
};

export const scenarioDeclined: OnboardingScenario = {
    name: "12. Declined",
    state: "Declined",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 12, 2026", submitted: "Apr 27, 2026" },
    declineReason:
        "Compliance flagged an issue with the beneficial-ownership chain. Our team will reach out by email with details and next steps.",
    banner: {
        kind: "action_required",
        headline: "Application declined. Please contact our compliance team.",
        ctaLabel: "Contact compliance"
    },
    subsections: allComplete(baselineSubsections()).map((sec) =>
        sec.id === "beneficial-ownership"
            ? {
                  ...sec,
                  status: "NeedsAttention" as const,
                  attentionReason: "Compliance was unable to verify a beneficial owner.",
                  completionSummary: undefined,
                  satisfiedProofPoints: sec.totalProofPoints - 2,
                  outstandingProofPoints: 2,
                  pendingReviewProofPoints: 0
              }
            : sec
    )
};

/** Path A applicant where all three §D.1 preconditions (§B.1 + §B.4 + §B.5)
 *  are Complete — the MSA card transitions from Locked to actionable, the
 *  Preconditions chip-list collapses to the "All preconditions met" line,
 *  and the nested §D.2 / §D.3 / §D.4 children become visible inside it. */
// ─── Per-slot review-state scenarios ────────────────────────────────────────
// These exercise the post-submission review flows defined in
// `intents/yaas-client/onboarding.md` ("Per-Slot Review State",
// "Information Requested (Adhoc requests)").

/** Information requested with two specific slot rejections — exercises
 *  the per-slot Rejected state with reviewer notes on real slots
 *  (not just section-level NeedsAttention). */
export const scenarioSlotRejections: OnboardingScenario = {
    name: "8. Slot rejections · 2 items",
    state: "InformationRequested",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 12, 2026", submitted: "Apr 27, 2026" },
    banner: {
        kind: "action_required",
        headline: "Information Requested. 2 items need your attention.",
        ctaLabel: "Review items",
        ctaTargetId: "legal-entity"
    },
    subsections: allReviewing(baselineSubsections()).map((sec) => {
        if (sec.id === "legal-entity") {
            // Reject slot 0 (Formation Document) with a reviewer note.
            const rejected = rejectSlot(
                sec,
                0,
                "The uploaded document is the Articles of Organization for a parent LLC, not the entity executing this MSA. Please upload the Certificate of Formation for the executing entity itself."
            );
            return {
                ...rejected,
                status: "NeedsAttention" as const,
                attentionReason: "Formation Document needs to be reuploaded for the executing entity.",
                slaLabel: undefined,
                lastUpdatedLabel: undefined,
                satisfiedProofPoints: 2,
                outstandingProofPoints: 1,
                pendingReviewProofPoints: 0
            };
        }
        if (sec.id === "signatory-authority") {
            // Reject slot 1 (Authorization Resolution) with a reviewer note.
            const rejected = rejectSlot(
                sec,
                1,
                "The Operating Agreement clause you submitted authorizes 'any Manager' to sign on behalf of the entity, but the signatory listed (Sarah Chen) holds a Member title, not a Manager title. Please upload either a board / manager resolution explicitly authorizing Sarah to sign this MSA, or an updated Operating Agreement excerpt that grants Members signing authority."
            );
            return {
                ...rejected,
                status: "NeedsAttention" as const,
                attentionReason: "Authorization Resolution needs clarification on signing authority.",
                slaLabel: undefined,
                lastUpdatedLabel: undefined,
                satisfiedProofPoints: 1,
                outstandingProofPoints: 1,
                pendingReviewProofPoints: 0
            };
        }
        return sec;
    })
};

/** Adhoc request — issuer added a new slot in §B.7 Source of Funds
 *  asking for audited financials. Spec slots all approved or pending;
 *  the new slot is the only outstanding action. */
export const scenarioAdhocRequest: OnboardingScenario = {
    name: "9. Adhoc request · supporting docs",
    state: "InformationRequested",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 12, 2026", submitted: "Apr 27, 2026" },
    banner: {
        kind: "action_required",
        headline: "Information Requested. The reviewer has asked for one additional document.",
        ctaLabel: "Review request",
        ctaTargetId: "source-of-funds"
    },
    subsections: allReviewing(baselineSubsections()).map((sec) => {
        if (sec.id === "source-of-funds") {
            const withRequest = withAdhocRequest(sec, {
                title: "Most recent audited financials",
                description:
                    "Your treasury narrative references a 2024 Series A raise. To strengthen the source-of-wealth picture for compliance review, please upload your most recent audited financial statements (FY2024 or FY2025 if available). PDF preferred.",
                requestedAt: "2026-04-30T14:22:00Z",
                deadline: "2026-05-07T23:59:59Z",
                severity: "minor"
            });
            return {
                ...withRequest,
                status: "NeedsAttention" as const,
                attentionReason: "Reviewer requested audited financial statements.",
                slaLabel: undefined,
                lastUpdatedLabel: "Requested Apr 30",
                outstandingProofPoints: 1
            };
        }
        return sec;
    })
};

/** Resubmitted — one slot was rejected, then resubmitted. Slot is back
 *  in `PendingReview` with a `versionHistory` entry showing the
 *  prior rejection. Demonstrates the version-history surface. */
export const scenarioResubmitted: OnboardingScenario = {
    name: "10. Resubmitted, awaiting re-review",
    state: "UnderReview",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 12, 2026", submitted: "Apr 27, 2026" },
    banner: {
        kind: "we_are_reviewing",
        headline: `We're reviewing your resubmitted document. Typically ${SLA}.`
    },
    subsections: allReviewing(baselineSubsections()).map((sec) => {
        if (sec.id === "legal-entity") {
            return withResubmittedSlot(sec, 0, {
                submittedAt: "2026-04-27T10:00:00Z",
                rejectedAt: "2026-04-29T15:30:00Z",
                rejectedReason:
                    "The uploaded document is the Articles of Organization for a parent LLC, not the entity executing this MSA. Please upload the Certificate of Formation for the executing entity itself."
            });
        }
        return sec;
    })
};

export const scenarioMsaReady: OnboardingScenario = {
    name: "5. MSA · preconditions met",
    state: "InProgress",
    orgName: ORG,
    sla: SLA,
    lifecycle: { started: "Apr 18, 2026" },
    banner: {
        kind: "your_turn",
        headline: "Your turn. The MSA is ready to sign.",
        ctaLabel: "Open MSA",
        ctaTargetId: "msa"
    },
    subsections: patch(baselineSubsections(), {
        "use-case": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 3,
            completionSummary: COMPLETION_SUMMARIES["use-case"]
        },
        "legal-entity": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 4,
            completionSummary: COMPLETION_SUMMARIES["legal-entity"]
        },
        "tax-identification": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 5,
            completionSummary: COMPLETION_SUMMARIES["tax-identification"]
        },
        "signatory-authority": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 4,
            completionSummary: COMPLETION_SUMMARIES["signatory-authority"]
        },
        "beneficial-ownership": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 12,
            completionSummary: COMPLETION_SUMMARIES["beneficial-ownership"]
        },
        "sanctions-screening": {
            status: "InProgress",
            slaLabel: "Solomon-run · awaiting submission"
        },
        "source-of-funds": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 3,
            completionSummary: COMPLETION_SUMMARIES["source-of-funds"]
        },
        "wallet-primary": {
            status: "Complete",
            estimateLabel: undefined,
            satisfiedProofPoints: 7,
            completionSummary: COMPLETION_SUMMARIES["wallet-primary"]
        },
        msa: {
            status: "WaitingOnYou",
            estimateLabel: "~5 min",
            attentionReason:
                "All three preconditions are met. Your authorized signatory can now e-sign the MSA, the embedded disclosures, and the R&Ws in a single envelope."
        }
    })
};

export const ALL_SCENARIOS: OnboardingScenario[] = [
    // Pre-submission
    scenarioFirstVisit,
    scenarioOrgInProgress,
    scenarioWalletsWaitingOnYou,
    scenarioMsaReady,
    // Post-submission active
    scenarioAllUnderReview,
    scenarioSomeApproved,
    scenarioSlotRejections,
    scenarioAdhocRequest,
    scenarioResubmitted,
    // Terminal
    scenarioApprovedCelebration,
    scenarioDeclined
];
